/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactElement, useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Container,
	GridLegacy,
	IconButton,
	Typography,
} from '@mui/material';
import CreateCourseView from './CreateCourse/CreateCourseView';
import CourseView from './Course/CoursesView';
import { FormattedMessage, useIntl } from 'react-intl';
import { useViewModel } from '../../../hooks/useViewModel';
import { CoursesOverviewViewModel } from './CoursesOverviewViewModel';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';
import { Course } from '../../../types/course/Course';
import CoursesOverviewStyles from './CoursesOverviewViewStyles';
import { NotificationCard } from '../../../types/notification/Notification';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import {
	DISMISSED_NOTIFICATIONS_KEY,
	DISMISSED_NOTIFICATIONS_RESET_EVENT,
	DISMISSED_NOTIFICATIONS_UPDATED_EVENT,
} from '../../../consts/notifications';

/**
 * Displays the course overview including the Strapi powered notification slider.
 * @returns Course overview page markup.
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
const CoursesOverviewView = observer((): ReactElement => {
	const viewModel: CoursesOverviewViewModel = useViewModel(CoursesOverviewViewModel);
	const { classes } = CoursesOverviewStyles();
	const intl = useIntl();
	const fallbackActionLabel = intl.formatMessage({ id: 'courses.notifications.default_action' });
	const [currentSlide, setCurrentSlide] = useState(0);

	// Load dismissed notifications from localStorage
	const loadDismissedNotifications = (): string[] => {
		try {
			const stored = localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
			return stored ? JSON.parse(stored) : [];
		} catch {
			return [];
		}
	};

	const [dismissedNotifications, setDismissedNotifications] = useState<string[]>(loadDismissedNotifications);

	// Save to localStorage whenever dismissedNotifications changes
	React.useEffect(() => {
		localStorage.setItem(DISMISSED_NOTIFICATIONS_KEY, JSON.stringify(dismissedNotifications));
		// Dispatch custom event so components like the navbar can update their badge.
		window.dispatchEvent(new Event(DISMISSED_NOTIFICATIONS_UPDATED_EVENT));
	}, [dismissedNotifications]);

	// Listen for reset event from navbar
	React.useEffect(() => {
		const handleReset = (): void => {
			setDismissedNotifications([]);
			setCurrentSlide(0);
		};

		window.addEventListener(DISMISSED_NOTIFICATIONS_RESET_EVENT, handleReset);

		return () => {
			window.removeEventListener(DISMISSED_NOTIFICATIONS_RESET_EVENT, handleReset);
		};
	}, []);

	autorun(() => {
		viewModel.subscribeToCourseEvents().forEach(() => {
			viewModel.queryCourses();
		});
	});

	const allNotifications = viewModel.getNotifications();
	// Filter out notifications that have an endDate in the past
	const activeNotifications = allNotifications.filter((n) => {
		if (!n.endDate) {
			// No endDate set, so show it
			return true;
		}
		// endDate is set, check if it's in the future
		return new Date(n.endDate) >= new Date();
	});
	const notifications = activeNotifications.filter((n) => !dismissedNotifications.includes(n.id));

	/**
	 * Handles dismissing a notification.
	 * @param notificationId The ID of the notification to dismiss.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const handleDismissNotification = (notificationId: string): void => {
		setDismissedNotifications((prev) => [...prev, notificationId]);
		// Adjust current slide if needed
		if (currentSlide >= notifications.length - 1 && currentSlide > 0) {
			setCurrentSlide(currentSlide - 1);
		}
	};

	/**
	 * Renders a single notification card.
	 * @param notification The notification data.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const renderNotificationCard = (notification: NotificationCard): ReactElement => (
		<Card className={classes.notificationCard} variant='outlined'>
			{/* Close Button */}
			<IconButton
				className={classes.notificationCloseButton}
				onClick={() => handleDismissNotification(notification.id)}
				size='small'
				aria-label='close'>
				<CloseIcon fontSize='small' />
			</IconButton>

			<CardContent className={classes.notificationContent}>
				<Typography variant='h6' className={classes.notificationTitle}>
					{notification.title}
				</Typography>
				{notification.description && (
					<Typography variant='body2' className={classes.notificationDescription}>
						{notification.description}
					</Typography>
				)}
			</CardContent>
			{notification.link && (
				<CardActions className={classes.notificationActions}>
					<Button
						component='a'
						href={notification.link}
						target='_blank'
						rel='noopener noreferrer'
						variant='contained'
						size='small'>
						{fallbackActionLabel}
					</Button>
				</CardActions>
			)}
		</Card>
	);

	/**
	 * Handles navigation to the previous slide.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const handlePrevSlide = (): void => {
		setCurrentSlide((prev) => (prev === 0 ? notifications.length - 1 : prev - 1));
	};

	/**
	 * Handles navigation to the next slide.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const handleNextSlide = (): void => {
		setCurrentSlide((prev) => (prev === notifications.length - 1 ? 0 : prev + 1));
	};

	/**
	 * Handles navigation to a specific slide via dot click.
	 * @param index The slide index to navigate to.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const handleDotClick = (index: number): void => {
		setCurrentSlide(index);
	};

	/**
	 * Renders the horizontal slider (or fallback copy) for notifications.
	 * @returns Slider element or an empty state message.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const renderNotificationSlider = (): ReactElement => {
		if (!notifications.length) {
			return null;
		}

		if (notifications.length === 1) {
			return (
				<Box className={classes.notificationSection}>
					<Box className={classes.sliderItem}>{renderNotificationCard(notifications[0])}</Box>
				</Box>
			);
		}

		return (
			<Box className={classes.notificationSection}>
				<Box className={classes.sliderContainer}>
					{/* Previous Button */}
					<IconButton
						className={classes.sliderNavButton}
						onClick={handlePrevSlide}
						sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>
						<ChevronLeftIcon />
					</IconButton>

					{/* Slider Track */}
					<Box className={classes.sliderTrack}>
						<Box
							className={classes.sliderContent}
							sx={{
								transform: `translateX(-${currentSlide * 100}%)`,
								transition: 'transform 0.3s ease-in-out',
							}}>
							{notifications.map((notification) => (
								<Box key={notification.id} className={classes.sliderItem}>
									{renderNotificationCard(notification)}
								</Box>
							))}
						</Box>
					</Box>

					{/* Next Button */}
					<IconButton
						className={classes.sliderNavButton}
						onClick={handleNextSlide}
						sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>
						<ChevronRightIcon />
					</IconButton>
				</Box>

				{/* Dots Navigation */}
				<Box className={classes.sliderDots}>
					{notifications.map((_, index) => (
						<Box
							key={index}
							className={classes.sliderDot}
							onClick={() => handleDotClick(index)}
							sx={{
								backgroundColor: currentSlide === index ? 'primary.main' : 'grey.400',
								cursor: 'pointer',
								'&:hover': {
									backgroundColor: currentSlide === index ? 'primary.dark' : 'grey.500',
								},
							}}
						/>
					))}
				</Box>
			</Box>
		);
	};

	return (
		<Container className={classes.root}>
			{renderNotificationSlider()}
			<Typography variant={'h1'}>
				<FormattedMessage id='courses.title' />
			</Typography>
			<GridLegacy container className={classes.gridContainer} spacing={2}>
				<GridLegacy item lg={2} md={2} sm={4} xs={6}>
					<CreateCourseView />
				</GridLegacy>
				{viewModel.getCourses().map((course: Course) => {
					return (
						<GridLegacy key={course.courseId} item lg={2} md={2} sm={4} xs={6}>
							<CourseView course={course} />
						</GridLegacy>
					);
				})}
			</GridLegacy>
		</Container>
	);
});

export default CoursesOverviewView;
